/**
 * The "Active Change Request" card of {@link ChangeRequestsTab}.
 *
 * Split out of ChangeRequestsTab (509 lines) for Customer review 11 Sep (review
 * msg 28). The markup is unchanged; the three per-field rows (date / price /
 * quantity), which were three copies of one block, render through
 * {@link ProposedFieldRow}. The tab only mounts this card when
 * `hasOpenChangeRequest` says a change is genuinely pending, so the per-field
 * accept / reject state lives here with it.
 */
import type { ReactNode } from 'react';
import { CheckCircle, XCircle, Clock, Calendar, Package, DollarSign, User, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PurchaseOrderLine } from '../../lib/api';
import { getOtifDateVariance } from '../../utils/otifDateVariance';
import { getStatusBadge, type ChangeField } from './changeRequestsTabHelpers';
import { useChangeRequestActions } from './useChangeRequestActions';
import { canActOnProposal, isPhantomChangeRequest, isNoOpDateProposal } from './pendingProposal';

interface ActiveChangeRequestCardProps {
  line: PurchaseOrderLine;
  /** The viewer raised this proposal (Cancel instead of Accept / Reject). */
  isProposer: boolean;
  /** The viewer may decide this proposal. */
  isReviewer: boolean;
  /** CR-7277: the single-proposal Accept / Reject pair, gated on an actionable payload. */
  showBulkActions: boolean;
  formatDate: (value: string) => string;
  formatDateTime: (value: string) => string;
  onAccept: () => void;
  onDecline: () => void;
  onUpdate?: () => void;
}

interface ProposedFieldRowProps {
  field: ChangeField;
  icon: LucideIcon;
  label: string;
  from: ReactNode;
  to: ReactNode;
  showActions: boolean;
  isProposer: boolean;
  loading: string | null;
  onAccept: () => void;
  onReject: () => void;
  onCancel: () => void;
  rejectForm: ReactNode;
}

/** One proposed field — from → to, plus Cancel (proposer) or Accept / Reject (reviewer). */
function ProposedFieldRow({
  field, icon: Icon, label, from, to, showActions, isProposer, loading, onAccept, onReject, onCancel, rejectForm,
}: ProposedFieldRowProps) {
  return (<>
    <div className="flex items-center gap-3 bg-white rounded-lg p-3 border">
      <Icon className="h-4 w-4 text-orange-600 flex-shrink-0" />
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-sm">
          <span className="text-muted-foreground">{from}</span>
          <span className="mx-2 text-muted-foreground">&rarr;</span>
          {to}
        </div>
      </div>
      {showActions && (
        <div className="flex items-center gap-1">
          {isProposer ? (
            <button onClick={onCancel} disabled={!!loading}
              className="flex items-center gap-1 px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-xs font-medium disabled:opacity-50">
              {loading === field ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
              Cancel
            </button>
          ) : (<>
            <button onClick={onAccept} disabled={!!loading}
              className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-medium disabled:opacity-50">
              {loading === field ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
              Accept
            </button>
            <button onClick={onReject} disabled={!!loading}
              className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium disabled:opacity-50">
              <XCircle className="h-3 w-3" /> Reject
            </button>
          </>)}
        </div>
      )}
    </div>
    {rejectForm}
  </>);
}

export default function ActiveChangeRequestCard({
  line, isProposer, isReviewer, showBulkActions, formatDate, formatDateTime, onAccept, onDecline, onUpdate,
}: ActiveChangeRequestCardProps) {
  // CR-4688 rework: the date row's "active" flag must come ONLY from
  // supplier_proposed_date. Once the backend accepts a date change, it
  // nulls supplier_proposed_date and keeps supplier_promise_date at the
  // accepted value — falling back to supplier_promise_date here made the
  // row stay "Open" even though the audit trail showed it as Accepted.
  const proposedDateTo = line.supplier_proposed_date || '';
  const proposedDateFrom = line.original_promise_date || line.delivery_date || '';
  const hasProposedDate = !!(proposedDateTo && proposedDateFrom && proposedDateTo !== proposedDateFrom);
  // CR-5479: classify the proposed date against the line's ODD (odd_date,
  // the date the verdict is measured against) + OTIF window so the row
  // carries the same amber/red + variance indicator as the PO Line Items
  // table and the grouped cards.
  const proposedDateVariance = getOtifDateVariance(
    proposedDateTo, line.odd_date ?? line.delivery_date, line.supplier_otif_window,
  );
  const hasProposedPrice = !!line.supplier_proposed_unit_price;
  const hasProposedQty = !!line.supplier_proposed_qty;
  const activeChangeFields = [hasProposedDate && 'date' as const, hasProposedPrice && 'price' as const, hasProposedQty && 'quantity' as const].filter(Boolean) as ChangeField[];
  const proposalCount = activeChangeFields.length;

  const {
    fieldActionLoading, rejectField, setRejectField, rejectReason, setRejectReason,
    handleAcceptField, handleRejectField, handleAcceptAll, handleCancelField,
  } = useChangeRequestActions(line, activeChangeFields, onUpdate);

  // CR-4688 round 3 rework: per-field actions whenever a specific field is in
  // play — the bulk modals are date-only and hit the date endpoint.
  // CR-7309: and only when the change is actionable — the gate the bulk pair
  // already had (CR-7277). The card decides this itself, whatever mounts it:
  // on ERP-imported lines supplier_proposed_date is the legacy Review Date
  // (CR-7294), and a date row drawn from it armed a per-field Accept that
  // rewrote the promise date on a line nobody had proposed anything for.
  const actionable = canActOnProposal(line);
  const showPerFieldActions = (isReviewer || isProposer) && actionable;
  const phantomChange = isPhantomChangeRequest(line);
  const noOpDateChange = isNoOpDateProposal(line);
  // Customer review 11 Sep (item 28): this card only mounts for a
  // change_proposed_* status, so the proposer is always known — the old
  // 'Unknown' fallback belonged to lines that never had a proposal.
  const proposedBy = line.acknowledge_status === 'change_proposed_customer' ? 'Customer' : 'Supplier';
  // CR-7166: role-appropriate label for date changes.
  const dateLabel = line.acknowledge_status === 'change_proposed_customer' ? 'Required Date' : 'Promise Date';

  // CR-4862: the reject reason form renders inline under the field being
  // rejected, or at the bottom for "Reject All".
  const renderRejectForm = (forField: ChangeField | 'all') => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
      <p className="text-sm font-medium text-red-800">
        {forField === 'all' ? 'Reject all changes' : `Reject ${forField === 'date' ? dateLabel : forField === 'price' ? 'Unit Price' : 'Quantity'} change`}
      </p>
      <input
        type="text"
        placeholder="Reason for rejection (required)"
        value={rejectReason}
        onChange={(e) => setRejectReason(e.target.value)}
        className="w-full px-3 py-2 border rounded text-sm"
        autoFocus
      />
      <div className="flex gap-2">
        <button
          onClick={handleRejectField}
          disabled={!rejectReason.trim() || !!fieldActionLoading}
          className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
        >
          {fieldActionLoading === forField ? <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> : null}
          Confirm Reject
        </button>
        <button
          onClick={() => { setRejectField(null); setRejectReason(''); }}
          className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const rowProps = (field: ChangeField) => ({
    field,
    showActions: showPerFieldActions,
    isProposer,
    loading: fieldActionLoading,
    onAccept: () => handleAcceptField(field),
    onReject: () => setRejectField(field),
    onCancel: () => handleCancelField(field),
    rejectForm: rejectField === field ? renderRejectForm(field) : null,
  });

  return (
    <div className="border-2 border-orange-300 rounded-lg bg-orange-50/50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-orange-100/60 border-b border-orange-200">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-orange-600" />
          <span className="font-semibold text-orange-800">Active Change Request</span>
        </div>
        {getStatusBadge('open')}
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <User className="h-3.5 w-3.5" />
          <span>
            {proposedBy}
            {line.acknowledged_by_details?.full_name && ` (${line.acknowledged_by_details.full_name})`}
            {line.acknowledge_date && ` on ${formatDateTime(line.acknowledge_date)}`}
          </span>
        </div>

        <div className="space-y-2">
          {hasProposedDate && (
            <ProposedFieldRow
              {...rowProps('date')}
              icon={Calendar}
              label={dateLabel}
              from={formatDate(proposedDateFrom)}
              // CR-5479: colour is reserved for early/late only.
              to={(
                <span className={proposedDateVariance?.className ? `inline-block px-1.5 py-0.5 rounded font-semibold ${proposedDateVariance.className}` : 'font-semibold'}>
                  {formatDate(proposedDateTo)}{proposedDateVariance?.suffix || ''}
                </span>
              )}
            />
          )}
          {hasProposedPrice && (
            <ProposedFieldRow
              {...rowProps('price')}
              icon={DollarSign}
              label="Unit Price"
              from={line.unit_price}
              to={<span className="font-semibold text-orange-700">{line.supplier_proposed_unit_price}</span>}
            />
          )}
          {hasProposedQty && (
            <ProposedFieldRow
              {...rowProps('quantity')}
              icon={Package}
              label="Quantity"
              from={line.quantity}
              to={<span className="font-semibold text-orange-700">{line.supplier_proposed_qty}</span>}
            />
          )}
          {/* CR-7277: say what the state actually is, and offer no action for it. */}
          {!hasProposedDate && !hasProposedPrice && !hasProposedQty && (
            <div className="bg-white rounded-lg p-3 border text-sm text-muted-foreground">
              {phantomChange
                ? 'This line is marked as having a change pending, but no proposed date, quantity or price was recorded against it. There is nothing to accept or reject — raise a new change request if one is needed.'
                : noOpDateChange
                  ? 'The proposed date is the same as the date already agreed, so there is nothing to change. There is nothing to accept or reject.'
                  : 'A change has been proposed. Review the details in the Detail tab.'}
            </div>
          )}
        </div>

        {/* CR-4862: "Reject All" spans every field, so its form stays at the bottom. */}
        {rejectField === 'all' && renderRejectForm('all')}

        {isReviewer && actionable && proposalCount > 1 && (
          <div className="flex items-center gap-3 pt-2 border-t border-orange-200">
            <button onClick={handleAcceptAll} disabled={!!fieldActionLoading} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50">
              {fieldActionLoading === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />} Accept All
            </button>
            <button onClick={() => setRejectField('all')} disabled={!!fieldActionLoading} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50">
              <XCircle className="h-4 w-4" /> Reject All
            </button>
          </div>
        )}

        {/* Bulk Accept / Reject — shown when only one proposal (backward compat) */}
        {showBulkActions && (
          <div className="flex items-center gap-3 pt-2 border-t border-orange-200">
            <button
              onClick={onAccept}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <CheckCircle className="h-4 w-4" />
              Accept
            </button>
            <button
              onClick={onDecline}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              <XCircle className="h-4 w-4" />
              Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
